import type { GameState, PlayerProfile, RoomMetadata, PlayerId, LeaderboardData } from "../../shared/types";

const BASE = "/api";

async function fetchAPI<T>(path: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: { "content-type": "application/json" },
    ...opts,
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export const api = {
  createSession: () =>
    fetchAPI<PlayerProfile>("/session", { method: "POST" }),

  listRooms: () =>
    fetchAPI<RoomMetadata[]>("/rooms"),

  createRoom: (data: {
    playerId: string; mode: "arcade" | "grid";
    complexity: "low" | "medium" | "high";
    timeLimitMs: number; aiDifficulty?: "easy" | "medium" | "hard";
    handicap?: "none" | "light" | "heavy";
  }) =>
    fetchAPI<RoomMetadata>("/rooms", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  joinRoom: (roomId: string, playerId: string) =>
    fetchAPI<RoomMetadata>(`/rooms/${roomId}/join`, {
      method: "POST",
      body: JSON.stringify({ playerId }),
    }),

  startGame: (data: {
    roomId: string; mode: "arcade" | "grid";
    complexity: "low" | "medium" | "high";
    timeLimitMs: number; aiDifficulty?: string;
    handicap?: "none" | "light" | "heavy";
  }) =>
    fetchAPI<GameState>("/games", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  getGame: (id: string, playerId?: PlayerId) =>
    fetchAPI<GameState>(`/games/${id}${playerId ? `?player=${playerId}` : ""}`),

  placeTile: (gameId: string, data: {
    player: PlayerId; tileId: string; letter: string; x: number; y: number;
  }) =>
    fetchAPI<GameState>(`/games/${gameId}/place`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  recallTile: (gameId: string, data: { player: PlayerId; tileId: string }) =>
    fetchAPI<GameState>(`/games/${gameId}/recall`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  // DEPRECATED: Real-time validation makes submission unnecessary
  submitBoard: (_gameId: string, _data: { player: PlayerId }) => {
    console.warn("submitBoard is deprecated. Game uses real-time validation.");
    return Promise.resolve({ state: {} as GameState, validation: { valid: true, errors: [] } });
  },

  getLeaderboard: (top?: number) =>
    fetchAPI<LeaderboardData>(`/leaderboard${top ? `?top=${top}` : ""}`),

  updateLeaderboard: (data: {
    playerId: string; score: number; playtime: number;
  }) =>
    fetchAPI<LeaderboardData>("/leaderboard", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  getReplay: (roomId: string) =>
    fetchAPI<import("../../shared/types").ReplayData>(`/replays/${roomId}`),
};
